import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { AuthService } from '@coin/shared/auth/data-access';
import { DirectsState, FirstVisitService, PermissionsService, UserState } from '@coin/shared/auth/feature';
import { LegacyHealthCheckService, StorageService } from '@coin/shared/core/data-access';
import { ContentLanguage, PermissionResource, StorageKey } from '@coin/shared/core/util-enums';
import { UpdateNotificationsService } from '@coin/shared/core/util-helpers';
import { GuestAccessDialogComponent } from '@coin/customer/shared/guest-access-feature';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { combineLatest, throwError } from 'rxjs';
import { catchError, find } from 'rxjs/operators';

@Component({
  selector: 'customer-app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false
})
export class AppComponent {
  private translate = inject(TranslateService);
  private store = inject(Store);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  authService = inject(AuthService);
  private guidedTour = inject(FirstVisitService);
  private healthCheckService = inject(LegacyHealthCheckService);
  private storageService = inject(StorageService);
  private destroyRef = inject(DestroyRef);
  private permissionsService = inject(PermissionsService);

  public appPages = [
    {
      title: 'Home',
      url: '/home',
      icon: 'home'
    },
    {
      title: 'List',
      url: '/list',
      icon: 'list'
    }
  ];
  showG2MPopup = true;
  public hideHeader = true;
  public isSidebarVisible = false;
  public healthcheckFail = false;
  public selectedSidebarItems: number[] = [0, 1];
  public showFirstVisitPopup = false;
  public permissionResource = PermissionResource;
  public isAuthenticated: boolean;
  public isAuthenticated$ = this.authService.isAuthenticated$;
  public isAuthorized$ = this.authService.isAuthorized$;

  private titles: string[] = ['Apps', 'Directs'];

  public get hasDirects(): boolean {
    return this.store.selectSnapshot(DirectsState.hasDirects);
  }

  public get filteredDetails(): string[] {
    return this.hasDirects ? this.titles : this.titles.filter(title => title !== 'Directs');
  }

  public get sideBarWidth(): string {
    return this.isAuthenticated ? (this.healthcheckFail ? '100%' : 'calc(100% - 55px)') : '100%';
  }

  constructor() {
    inject(UpdateNotificationsService).checkForUpdates();
    this.listenOnIsAuthenticated();
    this.listenToAuthorizedStatus();
    this.checkBackendStatus();
    this.initializeApp();
    this.hideHeader = window.location.href.includes('/login');
    this.setupLang();
    // only allow guided tour on dashboard
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      if (event instanceof NavigationEnd) {
        const isFirstVisitCompleted = this.storageService.get(StorageKey.FIRST_VISIT_COMPLETED);
        const firstVisit = isFirstVisitCompleted === 'true';
        this.showFirstVisitPopup = event.url === '/' && !firstVisit && this.isAuthenticated;
      }
    });
  }

  private listenToAuthorizedStatus(): void {
    this.authService.isAuthorized$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(isAuthorized => {
      if (!isAuthorized) {
        this.router.navigate(['unauthorized']);
      }
    });
  }

  private listenOnIsAuthenticated(): void {
    this.isAuthenticated$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(isAuthenticated => {
      this.isAuthenticated = isAuthenticated;
    });
  }

  private setupLang(): void {
    const lang = this.storageService.get(StorageKey.LANGUAGE);

    this.translate.use(lang || ContentLanguage.ENGLISH);
    this.translate.setDefaultLang(ContentLanguage.ENGLISH);
  }

  private checkBackendStatus(): void {
    this.healthCheckService
      .ping()
      .pipe(
        catchError(err => {
          this.healthcheckFail = true;
          this.router.navigate(['fallback'], { skipLocationChange: true });
          return throwError(err);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private initializeApp(): void {
    this.guidedTour.reopen.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.showFirstVisitPopup = true;
    });
    this.guidedTour.close.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.showFirstVisitPopup = false;
    });
    combineLatest([this.store.select(UserState.user), this.store.select(UserState.accessViews)])
      .pipe(
        find(([user, accessViews]) => !!user && !!accessViews),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (this.onlyHasGuestAccess()) {
          GuestAccessDialogComponent.openDialog(this.dialog);
        }
      });
  }

  private onlyHasGuestAccess(): boolean {
    return !this.permissionsService.hasPermissions(PermissionResource.CustomerApp);
  }

  public onSidebarToggle(): void {
    this.isSidebarVisible = !this.isSidebarVisible;
  }

  public onItemSelect(index: number): void {
    if (!this.selectedSidebarItems.includes(index)) {
      this.selectedSidebarItems.push(index);
    }
  }

  public onItemToggle(index: number): void {
    if (this.selectedSidebarItems.includes(index)) {
      this.selectedSidebarItems = this.selectedSidebarItems.filter(item => item !== index);
    } else {
      this.selectedSidebarItems.push(index);
    }
  }
}
